import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { blogPosts } from "../../data/blogData";

// Default images
import defaultHeroImage from "../../assets/images/blog/slug/hero.png";
import defaultBodyImage from "../../assets/images/blog/slug/body.png";
import defaultAvatarImage from "../../assets/images/blog/slug/avatar.jpg";

// Social share icons
import linkImage from "../../assets/images/blog/slug/social/link.png";
import facebookImage from "../../assets/images/blog/slug/social/fb.png";
import xImage from "../../assets/images/blog/slug/social/x.png";
import linkedinImage from "../../assets/images/blog/slug/social/linkdin.png";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for GSAP animations
  const titleRef = useRef(null);
  const authorRef = useRef(null);
  const heroImageRef = useRef(null);
  const contentRefs = useRef([]);
  const socialShareRef = useRef(null);
  const tagsRef = useRef(null);
  const relatedPostsRef = useRef(null);

  // First useEffect: Load blog post data
  useEffect(() => {
    window.scrollTo(0, 0);

    try {
      // Find the blog post by slug
      const post = blogPosts.find((post) => post.slug === slug);

      if (!post) {
        console.error(`Blog post not found for slug: ${slug}`);
        navigate("/blog", { replace: true });
        return;
      }

      // Get related posts (same category, excluding current post)
      const relatedPosts = blogPosts
        .filter((p) => p.category === post.category && p.slug !== slug)
        .slice(0, 3);

      // Ensure all required images are available
      const postWithImages = {
        ...post,
        image: post.image || defaultHeroImage,
        relatedPosts: relatedPosts.map((p) => ({
          ...p,
          image: p.image || defaultHeroImage,
        })),
      };

      setBlogPost(postWithImages);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error("Error loading blog post:", err);
      setError("Failed to load blog post");
      setLoading(false);
    }
  }, [slug, navigate]);

  // Second useEffect: Handle animations after blog post is loaded
  useEffect(() => {
    if (!blogPost || loading) return;

    gsap.registerPlugin(ScrollTrigger);

    // Helper function for parallax effect
    const createParallax = (element) => {
      if (!element) return;

      gsap.to(element, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: element.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    };

    // Apply parallax to all images
    const images = document.querySelectorAll("img:not(.rounded-full)"); // Exclude avatar images
    images.forEach((img) => {
      img.style.transform = "scale(1.1)";
      createParallax(img);
    });

    // Initial animations
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    if (titleRef.current) {
      tl.fromTo(
        titleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1 }
      );
    }

    if (authorRef.current) {
      tl.fromTo(
        authorRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        "-=0.5"
      );
    }

    if (heroImageRef.current) {
      tl.fromTo(
        heroImageRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2 },
        "-=0.5"
      );
    }

    // Content sections animations
    if (contentRefs.current.length > 0) {
      gsap.fromTo(
        contentRefs.current,
        {
          y: 30,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: contentRefs.current[0],
            start: "top 80%",
          },
        }
      );
    }

    // Social share and tags animation
    if (socialShareRef.current && tagsRef.current) {
      gsap.fromTo(
        [socialShareRef.current, tagsRef.current],
        {
          y: 20,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: socialShareRef.current,
            start: "top 80%",
          },
        }
      );
    }

    // Related posts animation
    if (relatedPostsRef.current) {
      gsap.fromTo(
        relatedPostsRef.current,
        {
          y: 30,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: relatedPostsRef.current,
            start: "top 80%",
          },
        }
      );
    }

    // Cleanup function to kill all ScrollTrigger instances
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [blogPost, loading]);

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = blogPost?.title || "";

    switch (platform) {
      case "copy":
        navigator.clipboard.writeText(url);
        // You might want to add a toast notification here
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url
          )}`
        );
        break;
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            url
          )}&text=${encodeURIComponent(title)}`
        );
        break;
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url
          )}`
        );
        break;
      default:
        break;
    }
  };

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12">
        <div className="text-center">
          <h2 className="text-2xl font-medium mb-4">Error Loading Post</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link
            to="/blog"
            className="inline-flex items-center text-black border border-black px-6 py-2.5 hover:bg-black hover:text-white transition-colors duration-300 text-sm tracking-wider font-medium"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="h-96 bg-gray-200 rounded mb-8"></div>
        </div>
      </div>
    );
  }

  if (!blogPost) return null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12">
      {/* Blog Header */}
      <div className="mb-6 sm:mb-8">
        <h1
          ref={titleRef}
          className="text-2xl sm:text-3xl md:text-4xl font-medium mb-4 sm:mb-6"
        >
          {blogPost.title}
        </h1>

        {/* Author Info */}
        <div ref={authorRef} className="flex items-center gap-3">
          <img
            src={blogPost.authorAvatar || defaultAvatarImage}
            alt={blogPost.author}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full"
          />
          <div className="flex flex-col text-black font-medium font-serif">
            <span>{blogPost.author}</span>
            <div className="flex items-center flex-wrap gap-2">
              <span>{blogPost.date}</span>
              <span className="mx-2 hidden sm:inline">•</span>
              <span>{blogPost.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div
        ref={heroImageRef}
        className="aspect-[3/2] sm:aspect-[2/1] md:aspect-[9/4] w-full mb-8 sm:mb-12 overflow-hidden"
      >
        <img
          src={blogPost.image || defaultHeroImage}
          alt={blogPost.title}
          className="w-full h-full object-cover bg-center bg-no-repeat"
        />
      </div>

      {/* Blog Content */}
      <div
        ref={(el) => (contentRefs.current[0] = el)}
        className="max-w-[800px] mx-auto border-b-2 border-black pb-8 sm:pb-12 mb-8 sm:mb-12"
      >
        <p className="text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 font-serif">
          {blogPost.description}
        </p>

        {/* Content Sections */}
        {blogPost.content?.sections.map((section, index) => (
          <div key={index} className="mb-12 sm:mb-16">
            <h2 className="text-lg sm:text-xl mb-4 sm:mb-6 font-serif">
              {section.title}
            </h2>
            <div className="text-sm sm:text-base leading-relaxed font-serif font-medium whitespace-pre-line mb-6 sm:mb-8">
              {section.content}
            </div>
            {section.image && (
              <div className="my-6 sm:my-8">
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={section.image}
                    alt={section.title}
                    className="w-full h-full object-cover bg-center bg-no-repeat"
                  />
                </div>
                <div className="mt-2 border-l-2 border-black pl-4 font-medium text-sm sm:text-base">
                  <p>Illustration for {section.title}</p>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Body Image - Only show if there's no content sections */}
        {!blogPost.content?.sections && (
          <div className="my-8 sm:my-12">
            <div className="aspect-[4/3] sm:aspect-[16/9] overflow-hidden">
              <img
                src={blogPost.bodyImage || defaultBodyImage}
                alt={blogPost.title || "Blog post illustration"}
                className="w-full h-full object-cover bg-center bg-no-repeat"
              />
            </div>
            <div className="mt-2 border-l-2 border-black pl-4 font-medium text-sm sm:text-base">
              <p>Featured image for {blogPost.title}</p>
            </div>
          </div>
        )}
      </div>

      {/* Social Share and Tags Section */}
      <div className="max-w-[800px] mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 sm:gap-0 mt-8 sm:mt-12 pt-6 sm:pt-8">
        {/* Social Share */}
        <div ref={socialShareRef} className="flex flex-col items-start gap-2">
          <span className="font-medium text-sm sm:text-base">
            Share this post
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleShare("copy")}
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={linkImage}
                alt="Copy Link"
                className="w-5 h-5 sm:w-6 sm:h-6"
              />
            </button>
            <button
              onClick={() => handleShare("linkedin")}
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={linkedinImage}
                alt="Share on LinkedIn"
                className="w-5 h-5 sm:w-6 sm:h-6"
              />
            </button>
            <button
              onClick={() => handleShare("twitter")}
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={xImage}
                alt="Share on X"
                className="w-5 h-5 sm:w-6 sm:h-6"
              />
            </button>
            <button
              onClick={() => handleShare("facebook")}
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={facebookImage}
                alt="Share on Facebook"
                className="w-5 h-5 sm:w-6 sm:h-6"
              />
            </button>
          </div>
        </div>

        {/* Tags */}
        <div ref={tagsRef} className="flex flex-wrap items-center gap-2">
          <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-gray-100 text-xs sm:text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer">
            {blogPost.category}
          </span>
        </div>
      </div>

      {/* Author Bio */}
      <div
        ref={(el) => (contentRefs.current[1] = el)}
        className="max-w-[800px] mx-auto flex items-center gap-3 mb-12 sm:mb-16"
      >
        <img
          src={blogPost.authorAvatar || defaultAvatarImage}
          alt={blogPost.author}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full"
        />
        <div className="flex flex-col text-black font-medium font-serif">
          <span className="text-sm sm:text-base">{blogPost.author}</span>
          <div>
            <span className="text-xs sm:text-sm text-gray-600">
              Technology & Architecture Expert
            </span>
          </div>
        </div>
      </div>

      {/* Related Posts Section */}
      {blogPost.relatedPosts?.length > 0 && (
        <div
          ref={relatedPostsRef}
          className="w-full px-4 sm:px-8 md:px-16 lg:px-24 py-12 sm:py-16 md:py-24 bg-white"
        >
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8 sm:mb-12">
              <div className="flex items-center gap-4 mb-6 sm:mb-8">
                <h3 className="text-sky-400 text-sm sm:text-base uppercase tracking-[0.2em] font-medium">
                  RELATED POSTS
                </h3>
                <div className="h-[1px] w-20 sm:w-32 bg-sky-400"></div>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium">
                More from {blogPost.category}
              </h2>
            </div>

            {/* Blog Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-8">
              {blogPost.relatedPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post.id} className="group">
                  <div className="aspect-[4/3] overflow-hidden mb-4 sm:mb-6">
                    <img
                      src={post.image || defaultHeroImage}
                      alt={post.title}
                      className="w-full h-full object-cover bg-center bg-no-repeat"
                    />
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center gap-4 text-xs sm:text-sm">
                      <span className="text-gray-500">{post.category}</span>
                      <span className="text-gray-500">{post.date}</span>
                    </div>
                    <h3 className="text-base sm:text-lg md:text-xl font-medium group-hover:text-sky-400 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>

            {/* See More Button */}
            <div className="flex justify-end">
              <Link
                to="/blog"
                className="inline-flex items-center text-black border border-black px-6 sm:px-8 py-2.5 sm:py-3 hover:bg-black hover:text-white transition-colors duration-300 text-xs sm:text-sm tracking-wider font-medium"
              >
                See All Posts
                <svg
                  className="ml-2 w-3.5 h-3.5 sm:w-4 sm:h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M13.5 19L20.5 12L13.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.5 12L3.5 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPostPage;
